import React, { useState, useEffect, useCallback } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Wifi, WifiOff, AlertTriangle, Cpu, Building2, Clock, RefreshCw,
  CheckCircle2, Signal, Upload
} from 'lucide-react';
import {
  getConectividadDashboard, getDispositivosOffline, getResumenAlertas,
  resolverAlertasMasivo, importarTelcoSims
} from '../../lib/gestion4satApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function fmt(n) { return (n || 0).toLocaleString('es-CL'); }

function ProgressBar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-2 bg-muted rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function G4sConectividadPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offlineDevices, setOfflineDevices] = useState([]);
  const [offlineTotal, setOfflineTotal] = useState(0);
  const [showOffline, setShowOffline] = useState(false);
  const [alertResumen, setAlertResumen] = useState(null);
  const [reimporting, setReimporting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [dash, alerts] = await Promise.all([
        getConectividadDashboard(),
        getResumenAlertas(),
      ]);
      setData(dash);
      setAlertResumen(alerts);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadOffline = async () => {
    try {
      const d = await getDispositivosOffline({ solo_activos: true });
      setOfflineDevices(d.dispositivos);
      setOfflineTotal(d.total);
      setShowOffline(true);
    } catch (e) { toast.error(e.message); }
  };

  const handleReimport = async (file) => {
    if (!file) return;
    setReimporting(true);
    try {
      const r = await importarTelcoSims(file);
      toast.success(`Telco actualizado: ${r.matched} emparejados, ${r.perdida_transmision || 0} alertas p\u00e9rdida`);
      load();
    } catch (e) { toast.error(e.message); }
    setReimporting(false);
  };

  const handleResolverTipo = async (tipo) => {
    try {
      const r = await resolverAlertasMasivo({ tipo, resolucion: 'Resuelto en lote desde panel conectividad' });
      toast.success(`${r.resueltas} alertas resueltas`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <G4sLayout><div className="space-y-4">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-24 w-full" />)}</div></G4sLayout>;

  const d = data || {};
  const pctOnline = d.pct_online || 0;

  return (
    <G4sLayout>
      <div className="space-y-6" data-testid="g4s-conectividad-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Conectividad</h1>
            <p className="text-sm text-muted-foreground">
              Monitoreo de transmisi&oacute;n y auditor&iacute;a de hardware
              {d.ultima_importacion && <span className="ml-2">| &Uacute;lt. import: {new Date(d.ultima_importacion).toLocaleDateString('es-CL')}</span>}
            </p>
          </div>
          <label>
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleReimport(e.target.files[0])} disabled={reimporting} data-testid="reimport-telco" />
            <Button variant="outline" asChild className="cursor-pointer">
              <span>{reimporting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Re-importar Telco</span>
            </Button>
          </label>
        </div>

        {/* Main gauge + KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="lg:col-span-1">
            <CardContent className="p-4 flex flex-col items-center justify-center">
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" className="text-muted" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor"
                    className={pctOnline >= 80 ? 'text-green-500' : pctOnline >= 50 ? 'text-amber-500' : 'text-red-500'}
                    strokeWidth="8" strokeDasharray={`${pctOnline * 2.64} 264`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-black">{pctOnline}%</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-2">Conectividad</div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Online</span>
              </div>
              <div className="text-2xl font-black text-green-600 tabular-nums">{d.online}</div>
              <div className="text-xs text-muted-foreground">{d.activos_online} asignados</div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-red-500/30 transition-colors" onClick={loadOffline}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <WifiOff className="h-4 w-4 text-red-500" />
                <span className="text-xs text-muted-foreground">Offline</span>
              </div>
              <div className="text-2xl font-black text-red-600 tabular-nums">{d.offline}</div>
              <div className="text-xs text-muted-foreground">{d.activos_offline} asignados</div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-orange-500/30 transition-colors" onClick={() => navigate('/gestion4sat/alertas')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-orange-500" />
                <span className="text-xs text-muted-foreground">P&eacute;rdida TX</span>
              </div>
              <div className="text-2xl font-black text-orange-600 tabular-nums">{d.alertas_perdida}</div>
              <div className="text-xs text-muted-foreground">pendientes</div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-red-500/30 transition-colors" onClick={() => navigate('/gestion4sat/alertas')}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Signal className="h-4 w-4 text-red-500" />
                <span className="text-xs text-muted-foreground">SIM Fantasma</span>
              </div>
              <div className="text-2xl font-black text-red-600 tabular-nums">{d.alertas_phantom}</div>
              <div className="text-xs text-muted-foreground">pendientes</div>
            </CardContent>
          </Card>
        </div>

        {/* Alertas summary + batch actions */}
        {alertResumen && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Resumen de Alertas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {Object.entries(alertResumen).map(([tipo, estados]) => {
                  const pendientes = estados.pendiente || 0;
                  const resueltas = estados.resuelta || 0;
                  const ignoradas = estados.ignorada || 0;
                  const total = pendientes + resueltas + ignoradas;
                  const label = tipo === 'sim_fantasma' ? 'SIM Fantasma' : tipo === 'perdida_transmision' ? 'P\u00e9rdida Transmisi\u00f3n' : tipo;
                  return (
                    <div key={tipo} className="p-3 rounded-lg border space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium capitalize">{label}</span>
                        <span className="text-xs text-muted-foreground">{total} total</span>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <Badge className="bg-amber-500/10 text-amber-600">{pendientes} pend.</Badge>
                        <Badge className="bg-green-500/10 text-green-600">{resueltas} resueltas</Badge>
                        <Badge className="bg-gray-500/10 text-gray-500">{ignoradas} ignor.</Badge>
                      </div>
                      {pendientes > 0 && (
                        <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => handleResolverTipo(tipo)} data-testid={`resolver-${tipo}`}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Resolver todas ({pendientes})
                        </Button>
                      )}
                    </div>
                  );
                })}
                {Object.keys(alertResumen).length === 0 && (
                  <p className="text-sm text-muted-foreground col-span-2 text-center py-4">Sin alertas en el sistema</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Offline by empresa */}
        {d.top_offline?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <WifiOff className="h-4 w-4 text-red-500" /> Empresas con m&aacute;s dispositivos Offline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {d.top_offline.map((e, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.razon_social}</div>
                    </div>
                    <Badge variant="destructive" className="bg-red-500/10 text-red-600 font-mono">{e.count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connectivity by empresa */}
        {d.por_empresa?.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 className="h-4 w-4 text-blue-500" /> Conectividad por Empresa (Top 20)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {d.por_empresa.map((e, i) => (
                  <div key={i} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium truncate max-w-[60%]">{e.razon_social}</span>
                      <span className="text-muted-foreground">
                        <span className="text-green-600">{e.online}</span>/{e.total}
                      </span>
                    </div>
                    <ProgressBar value={e.online} max={e.total} color="bg-green-500" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Offline devices detail */}
        {showOffline && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <WifiOff className="h-4 w-4 text-red-500" /> Dispositivos Offline ({offlineTotal})
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowOffline(false)}>Cerrar</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 text-left">IMEI</th>
                      <th className="p-2 text-left">Patente</th>
                      <th className="p-2 text-left">Empresa</th>
                      <th className="p-2 text-left hidden md:table-cell">ICCID</th>
                      <th className="p-2 text-left">Last Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {offlineDevices.slice(0, 50).map(d => (
                      <tr key={d.id} className="border-b">
                        <td className="p-2 font-mono">{d.imei}</td>
                        <td className="p-2 font-mono font-medium">{d.patente_formateada || '-'}</td>
                        <td className="p-2">{d.empresa_razon_social || '-'}</td>
                        <td className="p-2 font-mono hidden md:table-cell">{d.iccid ? d.iccid.slice(-8) : '-'}</td>
                        <td className="p-2 text-muted-foreground">{d.last_seen_at ? d.last_seen_at.slice(0, 10) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </G4sLayout>
  );
}
